import React, { useCallback, useEffect, useLayoutEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from 'react-bootstrap';
import ToggleSwitch from '../ToggleSwitch';
import useTierGate from '../../hooks/useTierGate';
import TrackArt from '../TrackArt';
import CoverPlayButton from '../CoverPlayButton';
import RowActions from '../RowActions';
import BulkRowContextMenu from '../BulkRowContextMenu';
import AddToPlaylistModal from '../AddToPlaylistModal';
import useRowSelection from '../../hooks/useRowSelection';
import { usePlayerRowSync, localPathFromMusicSrc } from '../../hooks/usePlayerRowSync';
import { setLocalListContext } from '../../utils/localListContext';
import { addTrack, setAutoPlay, resetAudioList } from '../../redux/slices/musicPlayerSlice';
import AutomixBookends, { WandIcon } from './AutomixBookends';
import { ReactComponent as AutomixIcon } from '../../assets/icons/categories/automix.svg';
import { ReactComponent as FolderIcon } from '../../assets/icons/select-folder.svg';
import { ReactComponent as WarningIcon } from '../../assets/icons/warning.svg';
import {
    removeTrack, moveTrack, setSetName, dismissError, setOrderMode, dismissRevealError,
    setOpenerKey, setCloserKey, isRevealShown,
} from '../../redux/slices/automixSlice';
import { setOrder, setOutDir, setStems } from '../../redux/slices/automixSettingsSlice';
import {
    buildAutomixSet, loadAutomixSets, loadAutomixEngine, rebuildWithoutOffenders,
    showAutomixOrder, stopAutomixOrder, resetAutomixOrder, closeAutomixSource,
    pickSetupCover, clearSetupCover,
} from '../../redux/thunk/automixThunk';
import AutomixCover from './AutomixCover';
import { AUTOMIX_MAX_TRACKS, automixIncluded, bpmRange, estimateBuildSec, formatBytes, formatClock, formatLength } from '../../utils/automixFormat';
import { track as trackEvent } from '../../utils/analytics';
import { artThumbUrl, DEFAULT_PLAYLIST_COVER } from '../../utils/cover';
import { getArtThumb, primeArtThumbs } from '../../utils/artThumbStore';

// A local file's cover, for the rows AND the bottom player — the player paints
// covers into CSS url('…'), so escape what
// encodeURIComponent leaves (! ' ( ) * ~) — a "(… Remix)" path came out blank.
// Same encoding as main's get-album-art-url.
const playerArt = (p) => `http://localhost:3069/album-art?path=${encodeURIComponent(p).replace(/[!'()*~]/g, (c) => `%${c.charCodeAt(0).toString(16).toUpperCase()}`)}`;
// A row's 32px art for a file the library doesn't know: the thumbnail variant
// (the route caches by id whenever it can place the file).
const pathThumb = (p) => `http://localhost:3069/album-art?path=${encodeURIComponent(p)}&s=1`;
const folderName = (p) => (p ? p.split(/[\\/]/).filter(Boolean).pop() : '');

// Deep analysis at a glance: what it buys, and how much of it is already done
// (the part that decides how long a build with it takes). The how is in the tooltip.
function stemsNote({ stemsCached = 0, present = 0 }) {
    if (present < 1) return '';
    const done = stemsCached >= present && present > 1
        ? `all ${present} tracks analysed`
        : `${Math.min(stemsCached, present)} of ${present} ${present === 1 ? 'track' : 'tracks'} analysed`;
    return `Avoids vocal clashes · ${done}`;
}

const ERROR_TITLES = {
    OUT_OF_SYNC: 'Some tracks couldn’t be beatmatched',
    TOO_FEW_TRACKS: 'Not enough tracks',
    ALREADY_RUNNING: 'A set is already being built',
    TIER_NOT_READY: 'Your plan couldn’t be checked',
    NO_TRANSITION: 'Two tracks can’t be mixed back to back',
};

const PencilIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
        <path d="M4 20h4L19 9l-4-4L4 16z" /><path d="M13.5 6.5l4 4" />
    </svg>
);

// the pinned ends, as a badge on the cover (no pill, no extra column)
const StartGlyph = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
        <path d="M5 5v14" /><path d="M9 12h10" /><path d="M15 8l4 4-4 4" />
    </svg>
);
const EndGlyph = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
        <path d="M19 5v14" /><path d="M5 12h10" /><path d="M11 8l4 4-4 4" />
    </svg>
);

const GripIcon = () => (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
        <circle cx="9" cy="6" r="1.6" /><circle cx="15" cy="6" r="1.6" />
        <circle cx="9" cy="12" r="1.6" /><circle cx="15" cy="12" r="1.6" />
        <circle cx="9" cy="18" r="1.6" /><circle cx="15" cy="18" r="1.6" />
    </svg>
);

const AddIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14" /><rect x="3" y="3" width="18" height="18" rx="2" /></svg>
);
const TrashIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-2 14H7L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" /></svg>
);

const prefersReducedMotion = () => !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

// "Analysing 12 of 40" / "Planning 20 of 40" from the engine's progress.
function revealDetail(progress) {
    const st = (progress?.stages || []).find((x) => x.status === 'active');
    if (!st) return 'Starting…';
    const verb = st.id === 'plan' ? 'Planning' : 'Analysing';
    return st.total > 1 ? `${verb} ${Math.min(st.done, st.total)} of ${st.total}` : `${verb}…`;
}

const AutomixSetup = () => {
    const dispatch = useDispatch();
    const automix = useSelector((s) => s.automix);
    const {
        source, tracks, tracksLoading, describing, setName, error, engine, orderMode, orderOrigin,
        openerKey, closerKey, revealPhase, revealProgress, revealError, coverPick,
    } = automix;
    // what the set would wear with no picture of its own: the playlist's, else
    // the mosaic of the tracks that have art (AutomixCover's own fallback)
    const sourceArtPaths = useMemo(() => tracks.map((t) => t.path).filter(Boolean).slice(0, 4), [tracks]);
    const settings = useSelector((s) => s.automixSettings);
    const { canAccess, requireUpgrade } = useTierGate('automix');
    const [preflight, setPreflight] = useState(null);
    // drag-to-reorder: the row being dragged and where it would land
    const [dragKey, setDragKey] = useState(null);
    const [dropAt, setDropAt] = useState(null);
    const dragKeyRef = useRef(null);
    const errorRef = useRef(null);
    const nameRef = useRef(null);
    const rowsRef = useRef(null);

    // Playback — the same pattern as the other Prepare tabs: a row plays
    // through the bottom player and the list is its play-through context.
    const [currentlyPlayingPath, setCurrentlyPlayingPath] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    usePlayerRowSync(currentlyPlayingPath, setIsPlaying, setCurrentlyPlayingPath);

    // Right-click: one row opens its ⋯ menu at the cursor; inside a
    // multi-row selection, the bulk menu.
    const [rowCtx, setRowCtx] = useState(null);     // { key, x, y, nonce }
    const [bulkCtx, setBulkCtx] = useState(null);   // { x, y }
    const ctxNonceRef = useRef(0);
    const [bulkPlaylistTargets, setBulkPlaylistTargets] = useState([]);

    const { included: usable, overLimit } = useMemo(() => automixIncluded(tracks), [tracks]);
    const includedKeys = useMemo(() => new Set(usable.map((t) => t.key)), [usable]);
    const missing = tracks.filter((t) => !t.path).length;
    const bestOrder = orderMode !== 'playlist';
    const shown = isRevealShown(automix);
    // Numbers are set positions: only when the list IS the running order.
    // Best order before a reveal is just the tracks (no numbers, no grips).
    const ordered = !bestOrder || shown;
    const revealing = revealPhase !== 'idle';
    // While MixGoat works out the order, the # column is what's being computed:
    // it shows (quietly pulsing) even over the pool, the tracks stay as they are.
    const numbered = ordered || revealing;
    const draggable = ordered && !revealing;
    // rows the engine refused to put side by side (a hand-made order)
    const flaggedPaths = useMemo(() => new Set((error?.code === 'NO_TRANSITION' ? error.pair || [] : []).map((p) => p.path).filter(Boolean)), [error]);
    const withStems = !!(settings.stems && engine?.stemsAvailable);
    const outDir = settings.outDir || preflight?.outDir || engine?.defaultOutDir;

    // A new error lands low in the (scrolling) rail: bring it, and the way
    // out it offers, into view.
    useEffect(() => {
        if (error) errorRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }, [error]);

    // What the build would involve (cached analysis, disk space) — asked again
    // whenever the tracklist or the folder changes.
    useEffect(() => {
        let alive = true;
        if (!usable.length) { setPreflight(null); return undefined; }
        window.electron.automix.preflight({
            tracks: usable.map((t) => ({ path: t.path, duration: t.duration })),
            outDir: settings.outDir || undefined,
        }).then((p) => { if (alive) setPreflight(p); }).catch(() => { if (alive) setPreflight(null); });
        return () => { alive = false; };
    }, [usable, settings.outDir]);

    // Row art the Library's way (utils/artThumbStore): one batch request for
    // every row's 64px thumb, kept in RAM for the session, so the covers paint
    // in the same tick as the rows — not one by one as each file is parsed. A
    // thumb not on disk yet is being extracted by the row's own request; the
    // store is asked again a few times while the list is open.
    const [, artArrived] = useReducer((n) => n + 1, 0);
    const idsKey = useMemo(() => tracks.map((t) => t.id).filter((id) => id != null).join(','), [tracks]);
    useEffect(() => {
        if (!idsKey) return undefined;
        let alive = true;
        const ids = idsKey.split(',').map(Number);
        let rounds = 0;
        const prime = async () => {
            const unknown = await primeArtThumbs(ids, () => { if (alive) artArrived(); });
            if (alive && unknown > 0 && rounds < 6) setTimeout(prime, 2000 * Math.min(++rounds, 4));
        };
        prime();
        return () => { alive = false; };
    }, [idsKey]);

    const rowKeys = useMemo(() => tracks.map((t) => t.key), [tracks]);
    const keyIndex = useMemo(() => new Map(rowKeys.map((k, i) => [k, i])), [rowKeys]);
    const byPath = useMemo(() => {
        const m = new Map();
        tracks.forEach((t) => { if (t.path && !m.has(t.path)) m.set(t.path, t); });
        return m;
    }, [tracks]);
    // the list in the order shown, as the player's play-through context
    const playablePaths = useMemo(() => [...byPath.keys()], [byPath]);
    const describe = useCallback((p) => {
        const t = byPath.get(p);
        return t ? { title: t.title, artist: t.artist, cover: t.cover || playerArt(p) } : {};
    }, [byPath]);

    const removeRows = useCallback((keys, via) => {
        if (!keys?.length) return;
        dispatch(removeTrack(keys));
        trackEvent('tracks_removed_from_list', { tab: 'automix', count: keys.length, via });
    }, [dispatch]);
    const scrollRowIntoView = useCallback((index, key) => {
        rowsRef.current?.querySelector(`[data-rowkey="${CSS.escape(key)}"]`)?.scrollIntoView({ block: 'nearest' });
    }, []);
    const onDeleteSelected = useCallback((keys) => removeRows(keys, 'delete_key'), [removeRows]);
    const { selected, orderedSelected, handleRowClick, selectOnly } = useRowSelection({
        keys: rowKeys, onDelete: onDeleteSelected, onArrowFocus: scrollRowIntoView, rowSelector: '.amx-row',
    });

    const handleRowContextMenu = (t, e) => {
        e.preventDefault();
        if (selected.size > 1 && selected.has(t.key)) { setBulkCtx({ x: e.clientX, y: e.clientY }); return; }
        selectOnly(t.key, keyIndex.get(t.key));
        setRowCtx({ key: t.key, x: e.clientX, y: e.clientY, nonce: ++ctxNonceRef.current });
    };

    // Coming back to the tab (or reopening the playlist): the row of whatever
    // the player already has loaded shows as the current one.
    useEffect(() => {
        if (currentlyPlayingPath) return;
        const player = window.musicPlayerInstance;
        const loaded = localPathFromMusicSrc(player?.src);
        if (loaded && byPath.has(loaded)) {
            setCurrentlyPlayingPath(loaded);
            setIsPlaying(!player.paused);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [byPath]);

    const playRow = async (t) => {
        if (!t.path) return;
        try {
            // the player already holds this track (paused): resume it — handing
            // it the same source again would leave it sitting paused
            const loaded = localPathFromMusicSrc(window.musicPlayerInstance?.src) === t.path;
            if ((currentlyPlayingPath === t.path && !isPlaying) || (loaded && window.musicPlayerInstance?.paused)) {
                setCurrentlyPlayingPath(t.path);
                window.musicPlayerInstance?.play();
                // resuming restores play-through-the-list, not a solo play
                setLocalListContext(dispatch, playablePaths, t.path, describe);
                setIsPlaying(true);
                dispatch(setAutoPlay(true));
                return;
            }
            const musicSrc = await window.electron.getAudioUrl(t.path);
            dispatch(resetAudioList());
            dispatch(addTrack({ name: t.title, singer: t.artist, cover: t.cover || playerArt(t.path), musicSrc }));
            setLocalListContext(dispatch, playablePaths, t.path, describe);
            dispatch(setAutoPlay(true));
            setCurrentlyPlayingPath(t.path);
            setIsPlaying(true);
        } catch (err) {
            console.error('[Automix] could not play the track:', err);
        }
    };
    const pauseRow = () => {
        window.musicPlayerInstance?.pause();
        setIsPlaying(false);
        dispatch(setAutoPlay(false));
    };
    const queueRow = async (t) => {
        if (!t.path) return;
        try {
            const musicSrc = await window.electron.getAudioUrl(t.path);
            dispatch(addTrack({ name: t.title, singer: t.artist, cover: t.cover || playerArt(t.path), musicSrc }));
        } catch (err) {
            console.error('[Automix] could not queue the track:', err);
        }
    };

    // The order changed under a playing track (a reveal, a drag, a mode
    // switch): "next" must follow the list as it is now.
    const playOrder = playablePaths.join('\n');
    const playOrderRef = useRef('');
    useEffect(() => {
        const prev = playOrderRef.current;
        playOrderRef.current = playOrder;
        if (!currentlyPlayingPath || !prev || prev === playOrder) return;
        const playerPath = localPathFromMusicSrc(window.musicPlayerInstance?.src);
        const anchor = playerPath && playablePaths.includes(playerPath) ? playerPath : currentlyPlayingPath;
        if (playablePaths.includes(anchor)) setLocalListContext(dispatch, playablePaths, anchor, describe);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [playOrder]);

    // When the order arrives (or the mode switches), the rows glide from where
    // they were to where they go — the reveal, made visible. Drags and removals
    // just land.
    const rowEls = useRef(new Map());
    const lastTops = useRef(new Map());
    const glideNext = useRef(false);
    const wasRevealing = useRef(revealing);
    const orderKey = rowKeys.join('|');
    useLayoutEffect(() => {
        const tops = new Map();
        rowEls.current.forEach((el, k) => tops.set(k, el.offsetTop));
        const fromReveal = wasRevealing.current && !revealing && shown;
        if ((glideNext.current || fromReveal) && !prefersReducedMotion()) {
            tops.forEach((top, k) => {
                const before = lastTops.current.get(k);
                if (before == null || before === top) return;
                rowEls.current.get(k)?.animate(
                    [{ transform: `translateY(${before - top}px)` }, { transform: 'translateY(0)' }],
                    { duration: 420, easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)' },
                );
            });
        }
        glideNext.current = false;
        lastTops.current = tops;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orderKey]);
    useLayoutEffect(() => { wasRevealing.current = revealing; }, [revealing]);

    const handleChooseFolder = async () => {
        const res = await window.electron.automix.chooseOutDir(outDir);
        if (res?.canceled || !res?.dir) return;
        dispatch(setOutDir(res.dir));
        // stems support and the shelf both depend on the folder
        dispatch(loadAutomixEngine());
        dispatch(loadAutomixSets());
    };

    const lowDisk = preflight && preflight.freeBytes != null && preflight.freeBytes < preflight.needBytes;
    let blocked = null;
    if (!tracksLoading && usable.length < 2) {
        blocked = tracks.length
            ? 'A set needs at least two tracks with audio files on this computer.'
            : 'This playlist is empty.';
    } else if (lowDisk) {
        blocked = `Building needs about ${formatBytes(preflight.needBytes)} free in this folder — ${formatBytes(preflight.freeBytes)} left. Choose another folder.`;
    }
    const buildSec = preflight ? estimateBuildSec({ ...preflight, withStems }) : 0;

    const handleBuild = () => {
        // GOAT: decided on click, never by disabling (a disabled button reads as broken)
        if (!canAccess) { requireUpgrade({ from: 'automix_build' }); return; }
        dispatch(buildAutomixSet());
    };

    const handleReveal = () => {
        if (!canAccess) { requireUpgrade({ from: 'automix_show_order' }); return; }
        dispatch(dismissError());
        dispatch(showAutomixOrder());
    };

    const pickMode = (mode) => {
        if (mode !== orderMode) glideNext.current = true;
        dispatch(setOrderMode(mode));   // this playlist
        dispatch(setOrder(mode));       // and the default for the next one
    };

    // the bar above the list: what the list shows, and how to change it
    let bar;
    if (revealing && bestOrder) {
        // progress + Stop live in the Start and finish line; the list shows it by
        // pulsing the positions being worked out
        bar = <span className="amx-bar-title">Revealing the order</span>;
    } else if (revealing) {
        const pct = Math.max(2, Math.round(revealProgress?.percent || 0));
        bar = (
            <>
                <span className="amx-bar-title">Revealing the order</span>
                <span className="amx-bar-progress" aria-hidden="true"><span style={{ width: `${pct}%` }} /></span>
                <span className="amx-bar-sub" aria-live="polite">{revealPhase === 'stopping' ? 'Stopping…' : revealDetail(revealProgress)}</span>
                <span className="amx-bar-sp" />
                <button type="button" className="amx-ghost" disabled={revealPhase === 'stopping'} onClick={() => dispatch(stopAutomixOrder())}>Stop</button>
            </>
        );
    } else if (revealError && !bestOrder) {
        bar = (
            <>
                <span className="amx-bar-title is-warn">Couldn’t reveal the order</span>
                <span className="amx-bar-sub" title={revealError.message}>{revealError.message}</span>
                <span className="amx-bar-sp" />
                <button type="button" className="amx-link is-quiet" onClick={() => dispatch(dismissRevealError())}>Dismiss</button>
                <button type="button" className="amx-ghost" onClick={handleReveal}>Try again</button>
            </>
        );
    } else if (bestOrder && !shown) {
        bar = (
            <>
                <span className="amx-bar-title">MixGoat picks the best order</span>
                <span className="amx-bar-sub">when you build</span>
            </>
        );
    } else if (bestOrder) {
        bar = (
            <>
                <span className="amx-bar-title">MixGoat’s best order</span>
                <span className="amx-bar-sub">Drag any track to make it yours</span>
            </>
        );
    } else {
        bar = (
            <>
                <span className="amx-bar-title">{orderOrigin === 'edited' ? 'Your order' : 'Playlist order'}</span>
                <span className="amx-bar-sub">Drag any track to change it</span>
                <span className="amx-bar-sp" />
                {orderOrigin === 'edited' && (
                    <button type="button" className="amx-link is-quiet" title="Back to the playlist’s order" onClick={() => { glideNext.current = true; dispatch(resetAutomixOrder()); }}>
                        Reset
                    </button>
                )}
                {usable.length >= 2 && (
                    <button type="button" className="amx-ghost" onClick={handleReveal}>
                        <WandIcon />
                        Reveal the best order
                    </button>
                )}
            </>
        );
    }

    // Where a dragged row lands: before or after the row under the pointer.
    const dropIndexFor = (e, index) => {
        const r = e.currentTarget.getBoundingClientRect();
        return e.clientY < r.top + r.height / 2 ? index : index + 1;
    };
    const endDrag = () => { setDragKey(null); setDropAt(null); dragKeyRef.current = null; };
    const dropTo = (toIndex) => {
        const key = dragKeyRef.current;
        if (!key) return;
        const from = tracks.findIndex((t) => t.key === key);
        // removing the row first shifts everything after it up by one
        const target = toIndex > from ? toIndex - 1 : toIndex;
        if (from !== target) {
            dispatch(moveTrack({ key, toIndex: target }));
            trackEvent('automix_tracks_reordered', { from, to: target, track_count: tracks.length, via: 'drag' });
        }
        endDrag();
    };
    const nudge = (t, index, delta) => {
        const target = index + delta;
        if (target < 0 || target >= tracks.length) return;
        dispatch(moveTrack({ key: t.key, toIndex: target }));
        trackEvent('automix_tracks_reordered', { from: index, to: target, track_count: tracks.length, via: 'keyboard' });
    };

    // the playlist's facts up top; the set's length belongs to the set (the rail)
    const meta = [
        `${usable.length} ${usable.length === 1 ? 'track' : 'tracks'}`,
        bpmRange(describing ? [] : usable),
    ].filter(Boolean).join(' · ');
    // while the files are being read, an unknown tempo/key/length is a cell
    // being worked on (the house pulse), not a dash
    const pending = (t) => (describing && t.path ? <span className="amx-n-pending" aria-hidden="true" /> : '—');

    return (
        <div className="amx-setup">
            <div className="upper-part source-bar-header amx-source-bar">
                <div className="source-bar">
                    <div className="source-bar-left">
                        <img className="amx-source-cover" src={source.cover || DEFAULT_PLAYLIST_COVER} alt="" />
                        <span className="source-bar-title" title={source.name}>{source.name}</span>
                        {!tracksLoading && <span className="source-bar-meta">{meta}</span>}
                        <button type="button" className="source-bar-clear" onClick={() => dispatch(closeAutomixSource())} title="Choose another playlist" aria-label="Choose another playlist">×</button>
                    </div>
                </div>
            </div>

            <div className="amx-setup-body">
                <div className={`amx-tracklist${numbered ? '' : ' is-pool'}${revealing ? ' is-revealing' : ''}`}>
                    {!tracksLoading && <div className={`amx-list-bar${revealing ? ' is-busy' : ''}`}>{bar}</div>}
                    <div className="amx-tracklist-head">
                        {numbered && <span className="col-n">#</span>}
                        {numbered && <span className="col-grip" />}
                        <span className="col-title">Track</span>
                        <span className="col-num">BPM</span>
                        <span className="col-num">Key</span>
                        <span className="col-num">Time</span>
                        <span className="col-more" />
                    </div>
                    <div className="amx-tracklist-rows" ref={rowsRef}>
                        {tracksLoading && Array.from({ length: 8 }).map((_, i) => (
                            <div key={`s${i}`} className="amx-row amx-row-skel" aria-hidden="true">
                                <span className="col-title"><span className="amx-skel-line" /></span>
                            </div>
                        ))}
                        {!tracksLoading && tracks.map((t, i) => {
                            const isIn = includedKeys.has(t.key);
                            const pin = bestOrder && isIn ? (t.key === openerKey ? 'start' : t.key === closerKey ? 'end' : null) : null;
                            const isCurrent = !!t.path && currentlyPlayingPath === t.path;
                            const playing = isCurrent && isPlaying;
                            // the thumb from RAM when primed; null there = the file has
                            // no art (nothing to fetch, the tile shows); not primed yet
                            // = the thumbnail URL, by library id or by path
                            const memThumb = t.id != null ? getArtThumb(t.id) : undefined;
                            const rowArt = memThumb !== undefined ? memThumb : (artThumbUrl(t.cover) || (t.path ? pathThumb(t.path) : null));
                            const rowClass = ['amx-row', isIn ? '' : 'is-missing', flaggedPaths.has(t.path) ? 'is-flagged' : '',
                                selected.has(t.key) ? 'is-selected' : '', isCurrent ? 'is-current' : '',
                                dragKey === t.key ? 'is-dragging' : '', dropAt === i ? 'drop-before' : '', dropAt === i + 1 && i === tracks.length - 1 ? 'drop-after' : '']
                                .filter(Boolean).join(' ');
                            return (
                                <div
                                    key={t.key}
                                    ref={(el) => { if (el) rowEls.current.set(t.key, el); else rowEls.current.delete(t.key); }}
                                    data-rowkey={t.key}
                                    className={rowClass}
                                    onClick={(e) => handleRowClick(i, t.key, e)}
                                    // shift-click extends the selection, not a text selection
                                    onMouseDown={(e) => { if (e.shiftKey) e.preventDefault(); }}
                                    onContextMenu={(e) => handleRowContextMenu(t, e)}
                                    onDoubleClick={(e) => { if (!e.target.closest('button')) playRow(t); }}
                                    onDragOver={draggable ? (e) => { e.preventDefault(); setDropAt(dropIndexFor(e, i)); } : undefined}
                                    onDrop={draggable ? (e) => { e.preventDefault(); dropTo(dropIndexFor(e, i)); } : undefined}
                                >
                                    {numbered && (
                                        <span className="col-n">
                                            {revealing ? <span className="amx-n-pending" aria-hidden="true" /> : isIn ? i + 1 : ''}
                                        </span>
                                    )}
                                    {numbered && !draggable && <span className="col-grip" />}
                                    {draggable && (
                                        <span className="col-grip">
                                            <button
                                                type="button"
                                                className="amx-grip"
                                                draggable
                                                aria-label={`Move ${t.title} (drag, or arrow keys)`}
                                                title="Drag to reorder"
                                                onDragStart={(e) => { dragKeyRef.current = t.key; setDragKey(t.key); e.dataTransfer.effectAllowed = 'move'; try { e.dataTransfer.setData('text/plain', t.key); } catch (err) { /* not needed */ } }}
                                                onDragEnd={endDrag}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'ArrowUp') { e.preventDefault(); nudge(t, i, -1); }
                                                    if (e.key === 'ArrowDown') { e.preventDefault(); nudge(t, i, 1); }
                                                }}
                                            >
                                                <GripIcon />
                                            </button>
                                        </span>
                                    )}
                                    <span className="col-title">
                                        <span className="amx-cover-wrap">
                                            {t.path ? (
                                                <CoverPlayButton
                                                    filePath={memThumb === null ? null : t.path}
                                                    cover={rowArt}
                                                    size={32}
                                                    isPlaying={playing}
                                                    title={playing ? 'Pause' : 'Play'}
                                                    onClick={(e) => { e.stopPropagation(); if (playing) pauseRow(); else playRow(t); }}
                                                />
                                            ) : (
                                                <TrackArt className="amx-row-cover" src={rowArt} size={32} />
                                            )}
                                            {pin && (
                                                <span className="amx-pin-badge" title={pin === 'start' ? 'Opens the set' : 'Closes the set'} aria-label={pin === 'start' ? 'Opens the set' : 'Closes the set'}>
                                                    {pin === 'start' ? <StartGlyph /> : <EndGlyph />}
                                                </span>
                                            )}
                                        </span>
                                        <span className="amx-row-text">
                                            <span className="amx-row-name" title={t.title}>{t.title}</span>
                                            <span className="amx-row-artist" title={t.artist}>{t.artist || '—'}</span>
                                        </span>
                                        {!t.path && <span className="amx-tag">{t.isMix ? 'Mix' : 'Not on this computer'}</span>}
                                        {t.path && !isIn && <span className="amx-tag">Over the {AUTOMIX_MAX_TRACKS}-track limit</span>}
                                    </span>
                                    <span className="col-num">{t.bpm ? Math.round(t.bpm) : pending(t)}</span>
                                    <span className="col-num">{t.musicalKey || pending(t)}</span>
                                    <span className="col-num">{t.duration ? formatClock(t.duration) : pending(t)}</span>
                                    <span className="col-more">
                                        <RowActions
                                            track={{ title: t.title, artist: t.artist, filePath: t.path, artwork: t.cover || null }}
                                            onAddToQueue={() => queueRow(t)}
                                            onRemoveFromLibrary={() => removeRows([t.key], 'menu')}
                                            removeLabel="Remove from set"
                                            openAtPoint={rowCtx && rowCtx.key === t.key ? rowCtx : null}
                                            fileMissing={!t.path}
                                            desktopLike
                                            analyticsFrom="automix"
                                        />
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                    {!tracksLoading && ((missing > 0 && !describing) || overLimit > 0) && (
                        <div className="amx-tracklist-foot">
                            {overLimit > 0 && `A set takes up to ${AUTOMIX_MAX_TRACKS} tracks — the first ${AUTOMIX_MAX_TRACKS} are in. Remove tracks to bring others in. `}
                            {missing > 0 && !describing && `${missing} ${missing === 1 ? 'track has' : 'tracks have'} no audio file on this computer and will be left out.`}
                        </div>
                    )}
                </div>

                <aside className="amx-rail">
                    <div className="amx-rail-scroll">
                        <div className="amx-set-head">
                            <span className="amx-setup-cover">
                                {/* what the set will actually be built wearing */}
                                {coverPick || (source.cover && source.cover !== DEFAULT_PLAYLIST_COVER)
                                    ? <img className="amx-cover" src={coverPick ? coverPick.preview : source.cover} alt="" style={{ width: 52, height: 52 }} />
                                    : <AutomixCover set={{ cover: null, artPaths: sourceArtPaths }} size={52} />}
                                <span className="amx-cover-tools">
                                    <button
                                        type="button"
                                        className="amx-cover-btn"
                                        title="Change the cover"
                                        aria-label="Change the cover"
                                        onClick={() => dispatch(pickSetupCover())}
                                    >
                                        <PencilIcon />
                                    </button>
                                </span>
                            </span>
                            <div className="amx-name-block">
                            <div className="amx-name">
                                <input
                                    ref={nameRef}
                                    type="text"
                                    aria-label="Set name"
                                    value={setName}
                                    maxLength={80}
                                    spellCheck={false}
                                    onChange={(e) => dispatch(setSetName(e.target.value))}
                                />
                                <button type="button" className="amx-name-edit" aria-label="Rename the set" title="Rename" onClick={() => nameRef.current?.select()}>
                                    <PencilIcon />
                                </button>
                            </div>
                            {preflight?.estimatedSetSec > 0 && !describing && <span className="amx-name-sub">≈ {formatLength(preflight.estimatedSetSec)} set</span>}
                            {coverPick && (
                                <button type="button" className="amx-link is-quiet amx-cover-reset" onClick={() => dispatch(clearSetupCover())}>
                                    Use the playlist’s cover
                                </button>
                            )}
                            </div>
                        </div>

                        <div className="amx-field">
                            <span className="amx-label">Order</span>
                            <div className="amx-segmented" role="group" aria-label="Running order">
                                {[['flow', 'Best order'], ['playlist', 'My order']].map(([val, lbl]) => (
                                    <button
                                        key={val}
                                        type="button"
                                        className={`amx-seg-btn${orderMode === val ? ' active' : ''}`}
                                        onClick={() => pickMode(val)}
                                    >
                                        {lbl}
                                    </button>
                                ))}
                            </div>
                            {!bestOrder && (
                                <p className="amx-help">
                                    Tracks play in the order on the left — drag them around to change it. Every transition is still planned for you.
                                </p>
                            )}
                        </div>

                        {bestOrder && usable.length >= 2 && (
                            <div className="amx-field">
                                <span className="amx-label">Start and finish</span>
                                <AutomixBookends
                                    tracks={usable}
                                    openerKey={includedKeys.has(openerKey) ? openerKey : null}
                                    closerKey={includedKeys.has(closerKey) ? closerKey : null}
                                    onOpener={(key) => dispatch(setOpenerKey(key))}
                                    onCloser={(key) => dispatch(setCloserKey(key))}
                                    reveal={{
                                        phase: revealPhase,
                                        percent: revealProgress?.percent,
                                        detail: revealDetail(revealProgress),
                                        shown,
                                        error: revealError,
                                        disabled: describing,   // the files are still being read
                                        onReveal: handleReveal,
                                        onStop: () => dispatch(stopAutomixOrder()),
                                    }}
                                />
                            </div>
                        )}

                        {error && (
                            <div className="amx-error" role="alert" ref={errorRef}>
                                <WarningIcon aria-hidden="true" focusable="false" />
                                <div>
                                    <strong>{ERROR_TITLES[error.code] || 'The set couldn’t be built'}</strong>
                                    {error.code === 'OUT_OF_SYNC' ? (
                                        <>
                                            <p>
                                                MixGoat couldn’t hold the beat steady through a blend, and a set with a drifting mix isn’t worth
                                                rendering — so nothing was.
                                            </p>
                                            {(error.offenders || []).length > 0 && (
                                                <ul>{error.offenders.map((o) => <li key={o.path}>{o.title}</li>)}</ul>
                                            )}
                                        </>
                                    ) : error.code === 'NO_TRANSITION' && error.pair?.length === 2 ? (
                                        <p>
                                            <strong className="amx-error-track">{error.pair[0].title}</strong> can’t go straight into{' '}
                                            <strong className="amx-error-track">{error.pair[1].title}</strong> — not even with a cut. Move one of them, or let
                                            MixGoat pick the order.
                                        </p>
                                    ) : <p>{error.message}</p>}
                                    <div className="amx-error-actions">
                                        {error.code === 'NO_TRANSITION' && (
                                            <button type="button" className="amx-link" onClick={handleReveal}>Reveal the best order</button>
                                        )}
                                        {error.code === 'OUT_OF_SYNC' && (error.offenders || []).length > 0 && (
                                            <button type="button" className="amx-link" onClick={() => dispatch(rebuildWithoutOffenders())}>
                                                Leave {error.offenders.length === 1 ? 'it' : 'them'} out and build again
                                            </button>
                                        )}
                                        <button type="button" className="amx-link is-quiet" onClick={() => dispatch(dismissError())}>Dismiss</button>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="amx-settings">
                            {engine?.stemsAvailable && (
                                <div className="amx-setting is-stacked">
                                    <ToggleSwitch
                                        id="amx-stems"
                                        checked={settings.stems}
                                        onChange={(v) => dispatch(setStems(v))}
                                        label="Deep analysis"
                                        tooltip="Finds the vocals, bass, drums and melody in every track, so blends avoid vocal clashes and double basslines."
                                    />
                                    {preflight && <span className="amx-setting-sub">{stemsNote(preflight)}</span>}
                                </div>
                            )}
                            <div className="amx-setting">
                                <span className="amx-setting-label">Save to</span>
                                <span className="amx-setting-sp" />
                                <button type="button" className="amx-folder-link" onClick={handleChooseFolder} title={outDir || ''}>
                                    <FolderIcon aria-hidden="true" focusable="false" />
                                    <span>{folderName(outDir)}</span>
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="amx-rail-cta">
                        <Button
                            className="button main-button amx-build-btn"
                            disabled={!!blocked || tracksLoading || describing}
                            onClick={handleBuild}
                        >
                            <AutomixIcon className="icon" aria-hidden="true" focusable="false" />
                            Build set
                        </Button>
                        {blocked
                            ? <p className="amx-help is-warn">{blocked}</p>
                            : describing
                                ? <p className="amx-build-note">Reading the tracks…</p>
                                : buildSec > 0 && (
                                <p className="amx-build-note">
                                    {/* the build's wait, not the set's length (that's in the header) */}
                                    Takes about {formatLength(buildSec)}. You can keep using MixGoat while it builds.
                                </p>
                            )}
                    </div>
                </aside>
            </div>
            {bulkCtx && orderedSelected.length > 1 && (() => {
                const paths = orderedSelected.map((k) => tracks.find((t) => t.key === k)?.path).filter(Boolean);
                return (
                    <BulkRowContextMenu
                        x={bulkCtx.x}
                        y={bulkCtx.y}
                        onClose={() => setBulkCtx(null)}
                        analyticsFrom="automix"
                        count={orderedSelected.length}
                        items={[
                            ...(paths.length ? [
                                { label: `Add ${paths.length} ${paths.length === 1 ? 'Track' : 'Tracks'} to Playlist`, icon: <AddIcon />, onClick: () => setBulkPlaylistTargets(paths) },
                                { divider: true },
                            ] : []),
                            { label: `Remove ${orderedSelected.length} Tracks from set`, icon: <TrashIcon />, danger: true, onClick: () => removeRows(orderedSelected, 'menu') },
                        ]}
                    />
                );
            })()}
            {bulkPlaylistTargets.length > 0 && (
                <AddToPlaylistModal
                    show
                    onHide={() => setBulkPlaylistTargets([])}
                    bulkFilePaths={bulkPlaylistTargets}
                    initialTab="add"
                    bulkLabel={`${bulkPlaylistTargets.length} tracks`}
                />
            )}
        </div>
    );
};

export default AutomixSetup;
